import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { clsx, safeRest } from '@wds/utils';
import { SEMANTIC_COLORS, DATA_VIZ_THEMES, KEYS } from '@wds/helpers';

import { useTheme } from 'react-jss';

import useStyles from './useStyles';

const ListItem = forwardRef(
  (
    {
      addOnLeft,
      addOnRight,
      affordance,
      children,
      color,
      component,
      disabled,
      id,
      index,
      isFocused,
      isNavigable,
      interactive,
      marked,
      navigationTarget,
      onClick,
      upgrade,
      ...other
    },
    ref
  ) => {
    // no reason to expose these in props
    const { accordion, expanded, paneComponent, ...rest } = other;

    const theme = useTheme();
    const {
      wrenchListRow,
      wrenchListItem,
      wrenchListLabel,
      wrenchListAddon,
      wrenchListAddonOnRight,
      wrenchListAffordance,
    } = useStyles({
      accordion,
      color,
      marked,
      disabled,
      upgrade,
      isFocused,
      interactive,
      addOnLeft,
      addOnRight,
      expanded,
      theme,
    });
    // const { wdsDefaultStyles } = DefaultStyles({ theme });
    const addOnClassNames = clsx(wrenchListAddon, wrenchListAddonOnRight);

    const href = isNavigable ? navigationTarget : undefined;
    const ListItemWrapper = isNavigable ? component || 'a' : 'div';
    const interactiveProps = interactive
      ? { tabIndex: disabled ? '-1' : 0, role: 'button' }
      : {};

    const onHandleKeyDown = (e) => {
      if (onClick && (e.key === KEYS.ENTER || e.key === KEYS.SPACE)) {
        onClick(e);
      }
    };

    return (
      <li className={wrenchListRow}>
        <ListItemWrapper
          disabled={disabled}
          className={wrenchListItem}
          onClick={onClick}
          onKeyDown={onHandleKeyDown}
          href={href}
          id={id}
          data-index={index}
          ref={ref}
          {...interactiveProps}
          {...safeRest(rest)}
        >
          <If condition={addOnLeft}>
            <div className={wrenchListAddon}>{addOnLeft}</div>
          </If>
          <span className={wrenchListLabel}>{children}</span>
          <If condition={addOnRight}>
            <div className={addOnClassNames}>{addOnRight}</div>
          </If>
          <If condition={affordance}>
            <span className={wrenchListAffordance}>{affordance}</span>
          </If>
        </ListItemWrapper>
        <If condition={typeof expanded !== 'undefined'}>{paneComponent}</If>
      </li>
    );
  }
);

ListItem.propTypes = {
  /**
   * To be used for Accordion Item
   */
  accordion: PropTypes.bool,
  /**
   * Left addons are used to support the label by supplying it more context and/or faster scanability
   * for users. Left addons must be consistent across an entire list. So if you add an avatar to one,
   * you must add an avatar to the rest.',
   */
  addOnLeft: PropTypes.node,
  /**
   * Right addons are used for secondary actions that can be taken or elements that provide a hint of extra content.
   * Unlike left addons, right addons do not need to persist throughout an entire list.
   * So if you use a button for a right addon you do not have to have one for each list item in the list.
   */
  addOnRight: PropTypes.node,
  /**
   * Add a custom affordance to the right of the ListItem
   */
  affordance: PropTypes.node,
  /**
   * Content of the ListItem
   */
  children: PropTypes.node,
  /**
   * Color variants for List Item
   */
  color: PropTypes.oneOfType([
    PropTypes.oneOf(SEMANTIC_COLORS),
    PropTypes.oneOf(DATA_VIZ_THEMES),
  ]),
  /**
   * In combination with `isNavigable` you can pass a custom component that will be
   * rendered in place of a regular a element. By default it is `a` if isNaviagable is true
   */
  component: PropTypes.func,
  /**
   * If true, disable specific ListItem
   */
  disabled: PropTypes.bool,
  /**
   * Identifier for ListItem component
   */
  id: PropTypes.string,
  /**
   * Data index of current ListItem
   */
  index: PropTypes.number,
  /**
   * If true, make a ListItem interactive enables hover and press-states. Most often only used in menus.
   */
  interactive: PropTypes.bool,
  /**
   * If true, ListItem will be focused on mount
   */
  isFocused: PropTypes.bool,
  /**
   * If true, ListItem can be a navigational component
   */
  isNavigable: PropTypes.bool,
  /**
   * If true, add a mark to the left-hand side of a list item.
   * Take notice that the label does not shift to accommodate for the mark; it stays in its original location.
   */
  marked: PropTypes.bool,
  /**
   * The href location of your nav target
   */
  navigationTarget: PropTypes.string,
  /**
   * onClick handler for ListItem
   * function(event: object) => void
   */
  onClick: PropTypes.func,
  /**
   * Add special "upgrade" highlight styles to item.
   */
  upgrade: PropTypes.bool,
};

ListItem.defaultProps = {
  accordion: false,
  addOnLeft: undefined,
  addOnRight: undefined,
  affordance: undefined,
  children: undefined,
  color: undefined,
  component: undefined,
  disabled: false,
  id: undefined,
  index: undefined,
  interactive: false,
  isFocused: false,
  isNavigable: false,
  marked: false,
  // eslint-disable-next-line no-script-url
  navigationTarget: 'javascript:void(0)',
  onClick: undefined,
  upgrade: false,
};

// The displayName is needed for component's Accordion and perhaps other consumers in the future
// https://reactjs.org/docs/forwarding-refs.html#displaying-a-custom-name-in-devtools
ListItem.displayName = 'ListItem';

export default ListItem;
